import { Box, IconButton, Typography } from '@mui/material';
import { theme } from 'common/theme/theme';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useMenuWidth } from 'common/hooks/use-menu-width';
import { useHeadHeight } from 'common/hooks/use-head-height';
import { useContentPadding } from 'common/hooks/use-content-padding';
import { getVideoAppRoutes } from 'common/constants/routes/routes-video';
import { getAttributesRoutes } from 'common/constants/routes/routes-attributes';
import { getCommonRoutes } from 'common/constants/routes/routes-common';
import { usePathsMatch } from 'common/hooks/use-paths-match';
import { userService } from 'common/services/user.service';
import { RetailerType } from 'common/constants/entities';
import { AuthUserControls } from 'common/ui/shared/auth-user-controls';
import { useVideoAppContext } from 'common/ui/containers/video-app-context';
import { Styles } from 'common/types/styles';
import { Menu } from './menu';
import { HEAD_PORTAL_ID } from './head-portal';
import { NotificationsButton, NotificationsContext, NotificationsPopup } from './notifications';

const productsPaths = ['/content/:pid', '/product/:pid', '/pdp/:pid'];

interface StylesProps {
  menuOpen: boolean;
  headHeight: number;
  menuWidth: number;
  paddingY: number;
  paddingX: number;
}

const getStyles = ({ menuOpen, headHeight, menuWidth, paddingX, paddingY }: StylesProps): Styles => ({
  container: { display: 'flex', height: '100%', backgroundColor: theme.palette.background.default },
  headerContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    boxSizing: 'border-box',
    paddingLeft: menuOpen ? `${menuWidth}px` : 0,
    transition: 'padding-left 0.225s ease-in-out',
    width: '100%',
  },
  contentContainer: {
    minHeight: `calc(100vh - ${headHeight}px)`,
    padding: theme.spacing(`${paddingY}px`, `${paddingX}px`),
    boxSizing: 'border-box',
  },
  headContainer: {
    display: headHeight === 0 ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FAFAFA',
    height: headHeight,
    padding: theme.spacing(0, 3.5),
    boxSizing: 'border-box',
    gap: 6,
  },
  pagePreviewContainer: { display: 'flex', alignItems: 'center', gap: 0.5 },
  pagePreview: { color: '#7F8299', fontSize: 14 },
  backIcon: { color: '#7F8299', width: 10, height: 10 },
  loadingContainer: {
    height: '100vh',
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  portal: { flexGrow: 1 },
  buttonsContainer: { display: 'flex', alignItems: 'center', gap: 1.5 },
  headVideoContainer: {
    display: headHeight === 0 ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: headHeight,
    padding: theme.spacing(1, 3.5),
    boxSizing: 'border-box',
    gap: 1.5,
  },
  contentVideoContainer: {
    minHeight: `calc(100vh - ${headHeight}px)`,
    padding: theme.spacing(0, `${paddingX}px`),
    boxSizing: 'border-box',
  },
});

export function Layout(): ReactElement {
  const location = useLocation();
  const nav = useNavigate();

  const forceNavigateToProducts = usePathsMatch(productsPaths);
  const { active: isVideoApp } = useVideoAppContext();

  const topAnchorRef = useRef<HTMLDivElement>();
  const notificationButton = useRef<HTMLButtonElement>();

  const [menuOpen, setMenuOpen] = useState<boolean>(true);
  const [notificationsOpen, setNotificationsOpen] = useState<boolean>(false);

  const headHeight = useHeadHeight();
  const menuWidth = useMenuWidth();
  const { paddingX, paddingY } = useContentPadding();

  const styles = getStyles({ menuOpen, headHeight, menuWidth, paddingX, paddingY });

  const currentPage = useMemo(() => {
    const appRoutes = isVideoApp ? getVideoAppRoutes() : getAttributesRoutes();
    const commonRoutes = getCommonRoutes();
    const routes = [...appRoutes, ...commonRoutes];
    const page = routes.find(r => matchPath(r.path, location.pathname)?.pathname);
    return page?.preview;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleMenu = () => {
    setMenuOpen(menuOpen => !menuOpen);
  };

  const handleOpenNotifications = () => {
    setNotificationsOpen(true);
  };

  const handleCloseNotifications = () => {
    setNotificationsOpen(false);
  };

  const getProductsListPath = () => {
    return userService.ensureRetailer(RetailerType.Walmart) ? '/content' : '/';
  };

  useEffect(() => {
    if (!topAnchorRef.current) return;

    setTimeout(() => {
      topAnchorRef.current.scrollIntoView({ behavior: 'smooth' });
    });
  }, [topAnchorRef, location.pathname]);

  return (
    <Box sx={styles.container}>
      <Menu open={menuOpen} />
      {!isVideoApp ? (
        <Box sx={styles.headerContentContainer}>
          <Box sx={styles.headContainer}>
            <Box sx={styles.pagePreviewContainer}>
              <IconButton onClick={() => (forceNavigateToProducts ? nav(getProductsListPath()) : nav(-1))} size="large">
                <ArrowBackIosNewIcon sx={styles.backIcon} />
              </IconButton>
              <Typography sx={styles.pagePreview}>{currentPage}</Typography>
            </Box>
            <Box sx={styles.portal} id={HEAD_PORTAL_ID} />
            <Box sx={styles.buttonsContainer}>
              <NotificationsContext>
                <NotificationsButton onClick={handleOpenNotifications} ref={notificationButton} />
                <NotificationsPopup
                  anchor={notificationButton.current}
                  onClose={handleCloseNotifications}
                  onOpen={handleOpenNotifications}
                  open={notificationsOpen}
                />
              </NotificationsContext>
              <AuthUserControls />
            </Box>
          </Box>
          <Box sx={styles.contentContainer}>
            <Outlet />
          </Box>
        </Box>
      ) : (
        <Box sx={styles.headerContentContainer}>
          <Box sx={styles.headVideoContainer}>
            <NotificationsContext>
              <NotificationsButton onClick={handleOpenNotifications} ref={notificationButton} />
              <NotificationsPopup
                anchor={notificationButton.current}
                onClose={handleCloseNotifications}
                onOpen={handleOpenNotifications}
                open={notificationsOpen}
              />
            </NotificationsContext>
            <AuthUserControls />
          </Box>
          <Box sx={styles.contentVideoContainer}>
            <Outlet />
          </Box>
        </Box>
      )}
    </Box>
  );
}
